import React, { useState } from 'react';

const FEEDBACK_EMAIL = 'feedback@velostruct.app';
const MAX_MAILTO_BODY = 1800;

interface FeedbackPanelProps {
  palette: {
    panelBg: string;
    panelBorder: string;
    panelText: string;
    panelSubText: string;
  };
  glass: React.CSSProperties;
  isOpen: boolean;
  onClose: () => void;
  getDiagramJson: () => string;
}

export function FeedbackPanel({ palette, glass, isOpen, onClose, getDiagramJson }: FeedbackPanelProps) {
  const [feedbackType, setFeedbackType] = useState<'bug' | 'feature'>('bug');
  const [description, setDescription] = useState('');
  const [includeDiagram, setIncludeDiagram] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!description.trim()) return;

    const subject = feedbackType === 'bug'
      ? '[Velostruct Bug] ' + description.slice(0, 60)
      : '[Velostruct Feature Request] ' + description.slice(0, 60);

    let body = `Type: ${feedbackType === 'bug' ? 'Bug Report' : 'Feature Request'}\n\n`;
    body += `Description:\n${description}\n\n`;
    body += `---\nSent from Velostruct Diagram Editor\n`;

    if (includeDiagram) {
      const json = getDiagramJson();
      if (json.length > MAX_MAILTO_BODY) {
        body += `\n[Diagram data too large for email (${json.length} chars). Please export and attach the .velostruct file manually.]\n`;
      } else {
        body += `\nDiagram Data:\n${json}\n`;
      }
    }

    const mailto = `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;

    setDescription('');
    setIncludeDiagram(false);
    onClose();
  };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div
        className="w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden"
        style={glass}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: palette.panelBorder }}
        >
          <span style={{ color: palette.panelText }} className="text-sm font-bold">
            Send Feedback
          </span>
          <button
            onClick={onClose}
            style={{ color: palette.panelSubText }}
            className="transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Type selector */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Feedback Type
            </label>
            <div className="flex gap-2">
              {(['bug', 'feature'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setFeedbackType(t)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: feedbackType === t ? '#4f46e5' : 'transparent',
                    borderColor: feedbackType === t ? '#4f46e5' : palette.panelBorder,
                    color: feedbackType === t ? 'white' : palette.panelText,
                  }}
                >
                  {t === 'bug' ? 'Bug Report' : 'Feature Request'}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Description
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={feedbackType === 'bug'
                ? 'Describe what happened and what you expected...'
                : 'Describe the feature you would like to see...'}
              className="w-full h-32 px-3 py-2 rounded-lg text-sm border resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
              style={{
                background: 'transparent',
                borderColor: palette.panelBorder,
                color: palette.panelText,
              }}
            />
          </div>

          {/* Include diagram */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeDiagram}
              onChange={e => setIncludeDiagram(e.target.checked)}
              className="w-4 h-4 rounded accent-indigo-500"
            />
            <span className="text-sm" style={{ color: palette.panelText }}>
              Include diagram data in email
            </span>
          </label>

          {/* Screenshot note */}
          <p className="text-[11px]" style={{ color: palette.panelSubText }}>
            To include a screenshot, please paste or attach it directly in your email client after clicking Send.
          </p>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!description.trim()}
            className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all"
            style={{
              background: description.trim() ? '#4f46e5' : palette.panelBorder,
              color: description.trim() ? 'white' : palette.panelSubText,
              cursor: description.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            Open Email Client
          </button>
        </div>
      </div>
    </div>
  );
}
